import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {'teaser' | 'reveal' | 'evergreen'} LaunchState
 * @typedef {'full' | 'short'} LaunchMotion
 *
 * @typedef {Object} LaunchSlideRefs
 * @property {HTMLElement} liveContent
 * @property {HTMLElement} [teaserContent]
 * @property {HTMLElement} [countdown]
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 *
 * @typedef {HTMLElement & { autoplay: boolean, paused: boolean, current: number, slides?: HTMLElement[], suspend(): void, play(): void }} SlideshowElement
 */

const AGE_GATE_ID = 'diageo-agegate-wrap';
const AGE_GATE_COOKIE = 'diageo-gateway';
const AGE_GATE_FALLBACK_MS = 4000;
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
const MOTION_DURATION_MS = { full: 3000, short: 1200 };
// After the slideshow moves away, wait this long before checking it has finished scrolling.
const LEAVE_DELAY_MS = 150;
// How long each launch effect runs, so autoplay waits for it and the editor preview tidies up after it.
const LAUNCH_EFFECT_MS = { confetti: 3000, fireworks: 7000, gold_rain: 3000, light_sweep: 3000 };

/**
 * Launch slide for the Responsive slideshow.
 * Picks the teaser / reveal / evergreen state from the launch date, runs the countdown,
 * waits for the age gate, and plays the entrance while briefly holding slideshow autoplay.
 *
 * @extends {Component<LaunchSlideRefs>}
 */
class LaunchSlide extends Component {
  requiredRefs = ['liveContent'];

  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {MutationObserver | undefined} */
  #ageGateObserver;
  #ageGatePending = false;
  /** @type {number | undefined} */
  #ageGateTimeout;
  /** @type {number | undefined} */
  #countdownInterval;
  /** @type {number | undefined} */
  #motionTimeout;
  #slideshowSuspended = false;
  /** Set after the first connect. Later connects are the slideshow moving this slide in the page. */
  #initialised = false;
  /** True while this slide is the slideshow's current slide, so repeat reports don't replay it. */
  #isCurrent = false;
  /** When the current entrance started (performance.now), to resume it after the slide is moved. */
  #playStartedAt = 0;
  /** @type {MutationObserver | undefined} */
  #settleObserver;
  /** @type {number | undefined} */
  #effectPreviewTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#slideshow?.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });
    document.addEventListener('shopify:block:select', this.#handleEditorSelect, { signal });

    // When the slideshow jumps more than one slide (e.g. looping back to the first), it moves the
    // target slide around the page, which disconnects and reconnects this element. Keep the current
    // state and countdown instead of replaying the entrance.
    if (this.#initialised) {
      // Moving the slide restarts its CSS animations; carry the entrance on from where it was.
      if ('playing' in this.dataset) {
        this.style.setProperty('--m-launch-elapsed', `${Math.round(performance.now() - this.#playStartedAt)}ms`);
      }
      if (this.dataset.state === 'teaser') this.#startCountdown();
      if (this.#ageGatePending) {
        this.#ageGatePending = false;
        this.#afterAgeGate(this.#playIfActive);
      }
      return;
    }

    this.#initialised = true;
    this.#setState(this.#resolveState());
    this.#afterAgeGate(this.#playIfActive);
    if (this.dataset.designMode === 'true' && this.#launchEffectChanged()) this.#previewLaunchEffect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#settleObserver?.disconnect();
    this.#ageGateObserver?.disconnect();
    clearTimeout(this.#ageGateTimeout);
    clearInterval(this.#countdownInterval);
    clearTimeout(this.#motionTimeout);
    clearTimeout(this.#effectPreviewTimeout);
    this.#resumeSlideshow();
  }

  get #slide() {
    return this.closest('slideshow-slide');
  }

  get #slideshow() {
    return /** @type {SlideshowElement | null} */ (this.closest('slideshow-component'));
  }

  get #isActiveSlide() {
    return this.#slide?.getAttribute('aria-hidden') !== 'true';
  }

  get #storageKey() {
    return `m-launch:${this.dataset.blockId}:${this.dataset.state}`;
  }

  /** @returns {LaunchState} */
  #resolveState() {
    const { preview, launchEpoch, revealDays } = this.dataset;
    if (preview === 'teaser' || preview === 'reveal' || preview === 'evergreen') return preview;

    const launchTime = Number(launchEpoch) * 1000;
    if (!launchTime) return 'reveal';

    const now = Date.now();
    if (now < launchTime) return 'teaser';
    if (now < launchTime + Number(revealDays) * DAY_MS) return 'reveal';
    return 'evergreen';
  }

  /** @param {LaunchState} state */
  #setState(state) {
    const { liveContent, teaserContent } = this.refs;

    this.dataset.state = state;
    liveContent.hidden = state === 'teaser';
    if (teaserContent) teaserContent.hidden = state !== 'teaser';

    clearInterval(this.#countdownInterval);
    if (state === 'teaser') this.#startCountdown();
    if (state === 'evergreen') delete this.dataset.armed;
  }

  #playIfActive = () => {
    if (!this.#isActiveSlide) return;
    this.#isCurrent = true;
    this.#playForState();
  };

  /** @param {LaunchMotion} [motion] */
  #playForState(motion) {
    if (this.dataset.state === 'evergreen' || prefersReducedMotion()) {
      delete this.dataset.armed;
      delete this.dataset.away;
      return;
    }

    this.#play(motion ?? (this.#hasPlayed() ? 'short' : 'full'));
  }

  /** @param {LaunchMotion} motion */
  #play(motion) {
    clearTimeout(this.#motionTimeout);
    // Hide the content while the animations reset, so the last frame of the previous entrance
    // never flashes before the new one starts.
    this.dataset.away = '';
    delete this.dataset.playing;
    this.#suspendSlideshow();
    this.#markPlayed();

    // Wait two frames so the reset is painted and the animations restart from the beginning.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dataset.motion = motion;
        this.style.setProperty('--m-launch-elapsed', '0ms');
        this.#playStartedAt = performance.now();
        delete this.dataset.armed;
        delete this.dataset.away;
        this.dataset.playing = '';
      });
    });

    // The launch effect only plays with the full entrance of the launch content; hold autoplay until it ends.
    const effectMs = motion === 'full' && this.dataset.state === 'reveal' ? this.#launchEffectDuration() : 0;
    this.#motionTimeout = window.setTimeout(
      () => this.#resumeSlideshow(),
      Math.max(MOTION_DURATION_MS[motion], effectMs)
    );
  }

  #launchEffectDuration() {
    const effect = /** @type {keyof typeof LAUNCH_EFFECT_MS} */ (this.dataset.launchEffect);
    return LAUNCH_EFFECT_MS[effect] ?? 0;
  }

  /**
   * Replays the entrance when the slideshow moves to this slide, and only then.
   *
   * The slideshow's select events are noisy: a plain click on the current slide reports a
   * zero-length "drag" to the same slide, and while scrolling it reports whichever slide is
   * closest, including the one being left. So: ignore reports that don't change the slide, act on
   * navigation (dots, arrows, autoplay) straight away, and decide scroll reports (swipes) only
   * once the scroll has settled.
   * @param {Event} event
   */
  #handleSlideSelect = (event) => {
    const { detail } = /** @type {import('@theme/events').SlideshowSelectEvent} */ (event);

    if (detail.trigger === 'scroll') {
      this.#whenSettled(this.#syncWithSlideshow);
      return;
    }

    if (detail.index === detail.previousIndex) return;

    if (detail.slide === this.#slide) {
      this.#becomeCurrent();
    } else {
      this.#leave();
    }
  };

  #becomeCurrent() {
    if (this.#isCurrent) return;

    this.#isCurrent = true;
    if (this.#ageGatePending) return;

    this.#playForState();
  }

  /**
   * The slideshow has moved away from this slide. Once it is off screen, hide the content and reset
   * the entrance, so next time the slide arrives it rises in fresh instead of showing its old final
   * frame first.
   */
  #leave() {
    if (!this.#isCurrent) return;

    this.#isCurrent = false;
    // Give the slideshow a moment to start scrolling before waiting for it to settle.
    window.setTimeout(() => {
      this.#whenSettled(() => {
        if (this.#isCurrent) return;

        this.dataset.away = '';
        delete this.dataset.playing;
      });
    }, LEAVE_DELAY_MS);
  }

  #syncWithSlideshow = () => {
    const slideshow = this.#slideshow;
    const currentSlide = slideshow?.slides?.[slideshow.current];

    if (currentSlide && currentSlide === this.#slide) {
      this.#becomeCurrent();
    } else {
      this.#leave();
    }
  };

  /**
   * Runs the callback once the slideshow has finished scrolling (it marks itself "transitioning").
   * @param {() => void} callback
   */
  #whenSettled(callback) {
    const slideshow = this.#slideshow;
    if (!slideshow?.hasAttribute('transitioning')) {
      callback();
      return;
    }

    this.#settleObserver?.disconnect();
    this.#settleObserver = new MutationObserver(() => {
      if (slideshow.hasAttribute('transitioning')) return;

      this.#settleObserver?.disconnect();
      callback();
    });
    this.#settleObserver.observe(slideshow, { attributes: true, attributeFilter: ['transitioning'] });
  }

  /** @param {Event} event */
  #handleEditorSelect = (event) => {
    const { detail } = /** @type {CustomEvent<{ blockId: string }>} */ (event);

    if (detail?.blockId !== this.dataset.blockId) return;

    this.#playForState('full');
    this.#previewLaunchEffect();
  };

  /**
   * Theme editor only: true when the Launch effect setting differs from the last render, so picking
   * a new effect previews it while edits to other settings don't.
   */
  #launchEffectChanged() {
    const key = `m-launch-effect:${this.dataset.blockId}`;
    const effect = this.dataset.launchEffect ?? 'none';

    try {
      const previous = sessionStorage.getItem(key);
      sessionStorage.setItem(key, effect);
      return previous !== null && previous !== effect;
    } catch {
      return false;
    }
  }

  /** Theme editor only: plays the launch effect on its own, whatever the slide is showing. */
  #previewLaunchEffect() {
    if (!this.dataset.launchEffect || this.dataset.launchEffect === 'none' || prefersReducedMotion()) return;

    clearTimeout(this.#effectPreviewTimeout);
    this.style.setProperty('--m-launch-elapsed', '0ms');
    delete this.dataset.effectPreview;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dataset.effectPreview = '';
      });
    });
    this.#effectPreviewTimeout = window.setTimeout(() => {
      delete this.dataset.effectPreview;
    }, this.#launchEffectDuration());
  }

  /**
   * Ticks the countdown (when shown) and switches to the launch content at the launch moment.
   * The timer runs even when the countdown is hidden, so the switch still happens on time.
   */
  #startCountdown() {
    const launchTime = Number(this.dataset.launchEpoch) * 1000;
    if (!launchTime) return;

    const { countdown, days, hours, minutes, seconds } = this.refs;
    const showCountdown = Boolean(countdown && days && hours && minutes && seconds);
    const animateTicks = showCountdown && countdown?.hasAttribute('data-tick') && !prefersReducedMotion();

    const tick = () => {
      const remaining = Math.max(0, launchTime - Date.now());

      if (showCountdown && days && hours && minutes && seconds) {
        const daysLeft = Math.floor(remaining / DAY_MS);
        const hoursLeft = Math.floor(remaining / HOUR_MS) % 24;
        const minutesLeft = Math.floor(remaining / MINUTE_MS) % 60;
        const secondsLeft = Math.floor(remaining / 1000) % 60;

        /** @type {Array<[HTMLElement, number, number]>} element, value, ring fill (0 to 1) */
        const units = [
          [days, daysLeft, Math.min(daysLeft, 30) / 30],
          [hours, hoursLeft, hoursLeft / 24],
          [minutes, minutesLeft, minutesLeft / 60],
          [seconds, secondsLeft, secondsLeft / 60],
        ];

        for (const [element, value, fill] of units) {
          const text = pad(value);
          if (element.textContent === text) continue;

          element.textContent = text;
          element.parentElement?.style.setProperty('--m-launch-progress', String(fill));
          if (animateTicks) {
            element.animate([{ transform: 'rotateX(-90deg)' }, { transform: 'none' }], {
              duration: 350,
              easing: 'ease-out',
            });
          }
        }
      }

      // A forced editor preview stays on the teaser even when the date has passed.
      if (remaining > 0 || this.dataset.preview) return;

      this.#setState('reveal');
      this.#playForState('full');
    };

    this.#countdownInterval = window.setInterval(tick, 1000);
    tick();
  }

  /**
   * Runs the callback once the visitor can actually see the page.
   * @param {() => void} callback
   */
  #afterAgeGate(callback) {
    if (this.dataset.ageGate !== 'true' || this.dataset.designMode === 'true' || hasPassedAgeGate()) {
      callback();
      return;
    }

    const signal = this.#abortController?.signal;
    let ageGateSeen = Boolean(document.getElementById(AGE_GATE_ID));

    this.#ageGatePending = true;
    this.dataset.waiting = '';

    const done = () => {
      if (!this.#ageGatePending || signal?.aborted) return;

      this.#ageGatePending = false;
      this.#ageGateObserver?.disconnect();
      clearTimeout(this.#ageGateTimeout);
      delete this.dataset.waiting;
      callback();
    };

    this.#ageGateObserver = new MutationObserver(() => {
      if (document.getElementById(AGE_GATE_ID)) {
        ageGateSeen = true;
        return;
      }

      if (ageGateSeen) done();
    });
    this.#ageGateObserver.observe(document.body, { childList: true });

    // The gate loads from an external script. If it never shows up, don't hold the slide back.
    this.#ageGateTimeout = window.setTimeout(() => {
      if (!ageGateSeen) done();
    }, AGE_GATE_FALLBACK_MS);
  }

  #suspendSlideshow() {
    const slideshow = this.#slideshow;
    if (!slideshow?.autoplay || slideshow.paused) return;

    slideshow.suspend();
    this.#slideshowSuspended = true;
  }

  #resumeSlideshow() {
    if (!this.#slideshowSuspended) return;

    this.#slideshowSuspended = false;
    const slideshow = this.#slideshow;
    if (!slideshow || slideshow.paused) return;

    slideshow.play();
  }

  #hasPlayed() {
    if (this.dataset.designMode === 'true') return false;

    try {
      return sessionStorage.getItem(this.#storageKey) === '1';
    } catch {
      return false;
    }
  }

  #markPlayed() {
    try {
      sessionStorage.setItem(this.#storageKey, '1');
    } catch {
      // Storage can be unavailable (private mode); the slide just plays in full again.
    }
  }
}

/** @returns {boolean} */
function hasPassedAgeGate() {
  const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(`${AGE_GATE_COOKIE}=`));
  if (!cookie) return false;

  try {
    return JSON.parse(decodeURIComponent(cookie.slice(AGE_GATE_COOKIE.length + 1))).pass === true;
  } catch {
    return false;
  }
}

/** @param {number} value */
function pad(value) {
  return String(value).padStart(2, '0');
}

if (!customElements.get('m-launch-slide')) {
  customElements.define('m-launch-slide', LaunchSlide);
}
